import { useState, type ComponentType } from "react";
import { NavLink, Outlet, useLocation, Link, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  ChevronLeft,
  LogOut,
  Menu,
  X,
  Building2,
  Users,
  ScrollText,
  Receipt,
  CreditCard,
  Activity,
  PackageOpen,
  Coins,
  Sparkles,
  ShieldCheck,
  Wrench,
  Settings,
} from "lucide-react";
import { useAuth, type EffectiveRole } from "../hooks/useAuth";
import { cn } from "../lib/utils";
import { OwnerAlerts } from "./owner/OwnerAlerts";

// A single navigation item.
export type NavItem = {
  to: string;
  label: string;
  icon?: ComponentType<{ className?: string }>;
  end?: boolean;
};

// Items grouped under a category header. Empty `header` collapses into a
// single bare list (used for short, role-specific menus).
export type NavSection = {
  header?: string;
  items: NavItem[];
};

// Sidebar nav definition per role. The category headings ("PAYMENTS", "API
// ACCESS", "SKILLS", "SETTINGS") match the way the user thinks about the
// three businesses in the app.
const ADMIN_NAV: NavSection[] = [
  {
    items: [
      { to: "/", label: "Overview", icon: LayoutDashboard, end: true },
      { to: "/admin/organizations", label: "Organizations", icon: Building2 },
      { to: "/admin/users", label: "Users", icon: Users },
    ],
  },
  {
    header: "Payments",
    items: [
      { to: "/admin/payments/proposals", label: "Proposals", icon: ScrollText },
      { to: "/admin/payments/invoices", label: "Invoices", icon: Receipt },
    ],
  },
  {
    header: "Subscriptions",
    items: [
      { to: "/admin/api/subscriptions", label: "Subscriptions", icon: CreditCard },
      { to: "/admin/api/plans", label: "Plans", icon: PackageOpen },
      { to: "/admin/api/model-pricing", label: "Model pricing", icon: Coins },
    ],
  },
  {
    header: "Skills",
    items: [
      { to: "/admin/skills", label: "Catalog", icon: Sparkles },
      { to: "/admin/skills/moderation", label: "Moderation", icon: ShieldCheck },
    ],
  },
  {
    header: "Audit",
    items: [{ to: "/admin/audit", label: "Audit logs", icon: ScrollText }],
  },
];

const OWNER_NAV: NavSection[] = [
  {
    items: [{ to: "/", label: "Overview", icon: LayoutDashboard, end: true }],
  },
  {
    header: "Subscription",
    items: [
      { to: "/api/subscription", label: "My plan", icon: CreditCard },
      { to: "/api/team", label: "Team", icon: Users },
      { to: "/api/usage", label: "Usage", icon: Activity },
      { to: "/billing", label: "Billing", icon: Receipt },
    ],
  },
  {
    header: "Skills",
    items: [
      { to: "/skills/catalog", label: "Catalog", icon: Sparkles },
      { to: "/skills/mine", label: "My skills", icon: Wrench },
    ],
  },
];

const MEMBER_NAV: NavSection[] = [
  {
    items: [{ to: "/", label: "Overview", icon: LayoutDashboard, end: true }],
  },
  {
    header: "Subscription",
    items: [{ to: "/api/usage", label: "Usage", icon: Activity }],
  },
  {
    header: "Skills",
    items: [{ to: "/skills/catalog", label: "Catalog", icon: Sparkles }],
  },
];

export function navFor(role: EffectiveRole): NavSection[] {
  switch (role) {
    case "admin":
      return ADMIN_NAV;
    case "enterprise_owner":
    case "institution":
      return OWNER_NAV;
    case "enterprise_member":
      return MEMBER_NAV;
    default:
      return [];
  }
}

export function AppLayout() {
  const { user, profile, signOut, effectiveRole, impersonation, stopImpersonation } =
    useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const sections = navFor(effectiveRole);
  const flatItems = sections.flatMap((s) => s.items);
  const showSettings = effectiveRole === "enterprise_owner";
  const currentItem = flatItems.find((n) =>
    n.end
      ? location.pathname === n.to
      : n.to !== "/" && location.pathname.startsWith(n.to),
  );

  return (
    <div className="min-h-screen bg-bg">
      {impersonation && (
        <div
          className="sticky top-0 z-50 px-4 py-2 flex items-center justify-center gap-3 text-sm"
          style={{
            backgroundColor: "rgba(200,169,81,0.15)",
            borderBottom: "1px solid rgba(200,169,81,0.3)",
          }}
        >
          <span className="text-ink">
            Viendo como{" "}
            <strong className="font-semibold">{impersonation.name}</strong>
          </span>
          <button
            onClick={stopImpersonation}
            className="inline-flex items-center gap-1 text-xs font-medium text-ink hover:opacity-80 bg-surface border border-border rounded-full px-3 py-1 cursor-pointer"
          >
            <X className="w-3.5 h-3.5" />
            Volver a admin
          </button>
        </div>
      )}
      <div className="flex">
        <aside className="hidden lg:flex w-64 shrink-0 flex-col border-r border-border bg-surface min-h-screen sticky top-0">
          <SidebarContent
            sections={sections}
            onSignOut={signOut}
            displayName={profile?.display_name ?? user?.email ?? ""}
            showSettings={showSettings}
          />
        </aside>

        {mobileOpen && (
          <div
            className="lg:hidden fixed inset-0 z-40 bg-ink/40 backdrop-blur-sm"
            onClick={() => setMobileOpen(false)}
          >
            <aside
              className="w-72 bg-surface h-full shadow-glass flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              <SidebarContent
                sections={sections}
                onSignOut={signOut}
                displayName={profile?.display_name ?? user?.email ?? ""}
                onClose={() => setMobileOpen(false)}
                showSettings={showSettings}
              />
            </aside>
          </div>
        )}

        <main className="flex-1 min-w-0">
          <div className="flex items-center gap-2 lg:hidden border-b border-border px-4 py-3">
            <button
              onClick={() => setMobileOpen(true)}
              className="p-1 text-ink-2 hover:text-ink"
            >
              <Menu className="w-5 h-5" />
            </button>
            <span className="text-sm font-medium text-ink">
              {currentItem?.label ?? "Cowboy"}
            </span>
          </div>
          <div className="p-6 lg:p-10 max-w-6xl">
            <OwnerAlerts />
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}

function SidebarContent({
  sections,
  onSignOut,
  displayName,
  onClose,
  showSettings,
}: {
  sections: NavSection[];
  onSignOut: () => void;
  displayName: string;
  onClose?: () => void;
  showSettings?: boolean;
}) {
  const navigate = useNavigate();
  return (
    <>
      <div className="px-5 pt-5 pb-4 border-b border-border">
        <h2 className="text-lg font-semibold text-ink">Cowboy</h2>
        <p className="text-xs text-ink-3 mt-1 truncate">{displayName}</p>
      </div>

      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-4">
        {sections.map((section, i) => (
          <div key={i}>
            {section.header && (
              <div className="px-3 mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-ink-3">
                {section.header}
              </div>
            )}
            <div className="space-y-0.5">
              {section.items.map(({ to, label, icon: Icon, end }) => (
                <NavLink
                  key={to}
                  to={to}
                  end={end}
                  onClick={onClose}
                  className={({ isActive }) =>
                    cn(
                      "flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors",
                      isActive
                        ? "bg-ink text-white"
                        : "text-ink-2 hover:bg-surface-2 hover:text-ink",
                    )
                  }
                >
                  {Icon && <Icon className="w-4 h-4" />}
                  {label}
                </NavLink>
              ))}
            </div>
          </div>
        ))}
      </nav>

      <div className="px-3 py-4 border-t border-border flex items-center gap-2">
        <button
          onClick={onSignOut}
          className="flex-1 flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-ink-2 hover:bg-surface-2 hover:text-ink transition-colors cursor-pointer"
        >
          <LogOut className="w-4 h-4" />
          Sign out
        </button>
        {showSettings && (
          <button
            onClick={() => {
              onClose?.();
              navigate("/billing");
            }}
            className="p-2 rounded-lg text-ink-2 hover:bg-surface-2 hover:text-ink transition-colors cursor-pointer"
            aria-label="Settings"
            title="Settings"
          >
            <Settings className="w-4 h-4" />
          </button>
        )}
      </div>
    </>
  );
}

export function PageHeader({
  title,
  subtitle,
  action,
  backTo,
}: {
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
  backTo?: string;
}) {
  return (
    <div className="mb-8">
      {backTo && (
        <Link
          to={backTo}
          className="inline-flex items-center gap-1 text-xs text-ink-3 hover:text-ink transition-colors mb-3"
        >
          <ChevronLeft className="w-3 h-3" />
          Back
        </Link>
      )}
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-3xl font-black text-ink leading-tight tracking-tight">
            {title}
          </h1>
          {subtitle && <p className="text-sm text-ink-3 mt-1">{subtitle}</p>}
        </div>
        {action}
      </div>
    </div>
  );
}
